import { MutableRefObject, RefObject, useCallback, useEffect, useState } from 'react'
import React from 'react'
import { createElement, defaultSizeForPlaceType } from '../elements/createElement'
import {
    clientToLogical,
    clampLogicalPoint,
    clampPlaceRect,
    collapseLinePlaceRect,
    dragExceededThreshold,
    horizontalLineEnds,
    linePlacementFromDrag,
} from '../elements/coords'
import { placeBoxAtPoint, placeBoxFromDrag, snapPlacePreviewRect } from '../elements/placementMetrics'
import { EditorTheme } from '../templateEditor.theme'
import { PlaceMode, PlaceModeOptions, PlaceToolId, Rect, TemplateElement } from '../templateEditor.types'

type PointerSession = {
    startX: number
    startY: number
    endX: number
    endY: number
    moved: boolean
    type: PlaceToolId
    opts: PlaceModeOptions
}

type PlacePreviewState = {
    rect: Rect
    opts: PlaceModeOptions
}

type UseTemplateEditorPlacementParams = {
    frameRef: RefObject<HTMLDivElement>
    zoomRef: MutableRefObject<number>
    theme: EditorTheme
    canvasW: number
    canvasH: number
    placeMode: PlaceMode
    setPlaceMode: (mode: PlaceMode) => void
    onElementCreated: (element: TemplateElement) => void
}

const startPointerSession = (onMove: (event: MouseEvent) => void, onUp: (event: MouseEvent) => void) => {
    const up = (event: MouseEvent) => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', up)
        onUp(event)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', up)
}

const isLinePlace = (type: PlaceToolId, opts: PlaceModeOptions) =>
    type === 'shape' && (opts.shapeKind ?? 'rect') === 'line'

const applyClickLinePlace = (
    type: PlaceToolId,
    opts: PlaceModeOptions,
    rect: Rect,
    canvasW: number,
    canvasH: number
): { box: Rect; opts: PlaceModeOptions } => {
    if (!isLinePlace(type, opts)) {
        return { box: rect, opts }
    }
    const collapsed = collapseLinePlaceRect(rect)
    const w = Math.min(Math.max(collapsed.w, 24), canvasW)
    const h = collapsed.h
    const box = {
        x: Math.min(Math.max(collapsed.x, 0), Math.max(0, canvasW - w)),
        y: Math.min(Math.max(collapsed.y, 0), Math.max(0, canvasH - h)),
        w,
        h,
    }
    return {
        box,
        opts: { ...opts, lineEnds: horizontalLineEnds(box) },
    }
}

export const useTemplateEditorPlacement = ({
    frameRef,
    zoomRef,
    theme,
    canvasW,
    canvasH,
    placeMode,
    setPlaceMode,
    onElementCreated,
}: UseTemplateEditorPlacementParams) => {
    const [preview, setPreview] = useState<PlacePreviewState | null>(null)

    const exitPlaceMode = useCallback(() => {
        setPlaceMode(null)
        setPreview(null)
    }, [setPlaceMode])

    const enterPlaceMode = useCallback(
        (type: PlaceToolId, opts: PlaceModeOptions = {}) => {
            if (type === 'image' || type === 'video') {
                const sameMedia =
                    placeMode?.type === type &&
                    placeMode.opts.sourceId === opts.sourceId &&
                    placeMode.opts.src === opts.src

                if (sameMedia) {
                    exitPlaceMode()
                    return
                }

                setPlaceMode({ type, opts: { ...opts } })
                setPreview(null)
                return
            }

            if (type === 'shape') {
                const nextKind = opts.shapeKind ?? 'rect'
                const sameShape = placeMode?.type === 'shape' && (placeMode.opts.shapeKind ?? 'rect') === nextKind

                if (sameShape) {
                    exitPlaceMode()
                    return
                }

                setPlaceMode({ type, opts: { ...opts, shapeKind: nextKind } })
                setPreview(null)
                return
            }

            if (placeMode?.type === type) {
                exitPlaceMode()
                return
            }

            setPlaceMode({ type, opts: { ...opts } })
            setPreview(null)
        },
        [exitPlaceMode, placeMode, setPlaceMode]
    )

    const togglePlaceMode = useCallback(
        (type: PlaceToolId) => {
            enterPlaceMode(type)
        },
        [enterPlaceMode]
    )

    const commitPlacement = useCallback(
        (session: PointerSession) => {
            const frame = frameRef.current
            if (!frame) {
                return
            }

            const frameRect = frame.getBoundingClientRect()
            const zoom = zoomRef.current
            const start = clientToLogical(session.startX, session.startY, frameRect, theme.displayScale, zoom)
            const end = clientToLogical(session.endX, session.endY, frameRect, theme.displayScale, zoom)
            const clampedStart = clampLogicalPoint(start.x, start.y, canvasW, canvasH)
            const clampedEnd = clampLogicalPoint(end.x, end.y, canvasW, canvasH)

            let box: Rect
            let opts = session.opts

            if (isLinePlace(session.type, session.opts) && session.moved) {
                const placed = linePlacementFromDrag(
                    clampedStart.x,
                    clampedStart.y,
                    clampedEnd.x,
                    clampedEnd.y,
                    canvasW,
                    canvasH
                )
                box = placed.rect
                opts = { ...session.opts, lineEnds: placed.lineEnds }
            } else if (!session.moved) {
                const def = defaultSizeForPlaceType(session.type, session.opts, theme)
                const minSize = isLinePlace(session.type, session.opts) ? 4 : theme.minElSize
                box = placeBoxAtPoint(clampedStart.x, clampedStart.y, def.w, def.h, canvasW, canvasH, minSize)
                const lineClick = applyClickLinePlace(session.type, session.opts, box, canvasW, canvasH)
                box = lineClick.box
                opts = lineClick.opts
            } else {
                box = placeBoxFromDrag(
                    clampedStart.x,
                    clampedStart.y,
                    clampedEnd.x,
                    clampedEnd.y,
                    canvasW,
                    canvasH,
                    theme.minElSize
                )
                box = snapPlacePreviewRect(session.type, box, theme)
                box = clampPlaceRect(box, canvasW, canvasH, theme.minElSize, theme)
            }

            const element = createElement({
                type: session.type,
                box,
                center: !session.moved ? clampedStart : undefined,
                opts,
                theme,
                index: undefined,
            })

            onElementCreated(element)
            exitPlaceMode()
        },
        [canvasH, canvasW, exitPlaceMode, frameRef, onElementCreated, theme, zoomRef]
    )

    const startPlaceDrag = useCallback(
        (event: React.MouseEvent) => {
            if (!placeMode || event.button !== 0) {
                return false
            }

            event.preventDefault()
            event.stopPropagation()

            const session: PointerSession = {
                startX: event.clientX,
                startY: event.clientY,
                endX: event.clientX,
                endY: event.clientY,
                moved: false,
                type: placeMode.type,
                opts: { ...placeMode.opts },
            }

            const onMove = (moveEvent: MouseEvent) => {
                session.endX = moveEvent.clientX
                session.endY = moveEvent.clientY

                if (
                    !session.moved &&
                    dragExceededThreshold(
                        session.startX,
                        session.startY,
                        moveEvent.clientX,
                        moveEvent.clientY,
                        theme.dragThreshold
                    )
                ) {
                    session.moved = true
                }

                if (!session.moved || !frameRef.current) {
                    setPreview(null)
                    return
                }

                const frameRect = frameRef.current.getBoundingClientRect()
                const zoom = zoomRef.current
                const start = clientToLogical(session.startX, session.startY, frameRect, theme.displayScale, zoom)
                const end = clientToLogical(moveEvent.clientX, moveEvent.clientY, frameRect, theme.displayScale, zoom)

                if (isLinePlace(session.type, session.opts)) {
                    const placed = linePlacementFromDrag(start.x, start.y, end.x, end.y, canvasW, canvasH)
                    setPreview({
                        rect: placed.rect,
                        opts: { ...session.opts, lineEnds: placed.lineEnds },
                    })
                    return
                }

                let rect = placeBoxFromDrag(start.x, start.y, end.x, end.y, canvasW, canvasH, theme.minElSize)
                rect = snapPlacePreviewRect(session.type, rect, theme)
                rect = clampPlaceRect(rect, canvasW, canvasH, theme.minElSize, theme)

                setPreview({
                    rect,
                    opts: session.opts,
                })
            }

            const onUp = () => {
                commitPlacement(session)
                setPreview(null)
            }

            startPointerSession(onMove, onUp)
            return true
        },
        [canvasH, canvasW, commitPlacement, frameRef, placeMode, theme, zoomRef]
    )

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && placeMode) {
                event.preventDefault()
                event.stopImmediatePropagation()
                exitPlaceMode()
            }
        }

        window.addEventListener('keydown', onKeyDown, true)
        return () => window.removeEventListener('keydown', onKeyDown, true)
    }, [exitPlaceMode, placeMode])

    return {
        placeMode,
        previewRect: preview?.rect ?? null,
        previewOpts: preview?.opts ?? placeMode?.opts ?? {},
        enterPlaceMode,
        exitPlaceMode,
        togglePlaceMode,
        startPlaceDrag,
        isPlacing: Boolean(placeMode),
    }
}
